interface EntryPointOptions {
  locationSearch?: string;
  referrer?: string;
  serverReferer?: string;
}

const HOMEPAGE_MODE_BY_PARAM: Record<string, string> = {
  rent: 'Homepage - Rent',
  sold: 'Homepage - Sold',
};

const HOMEPAGE_VIEW_BY_PATH: Record<string, string> = {
  '/new-homes': 'New Homes',
  '/rural': 'Rural',
  '/house-and-land': 'House and Land',
  '/retirement': 'Retirement',
};

const MODE_BY_PATH_SEGMENT: Record<string, string> = {
  sale: 'Buy',
  rent: 'Rent',
  'sold-listings': 'Sold',
};

const CONTENT_VERTICAL_BY_PATH_SEGMENT: Record<string, string> = {
  news: 'News',
  advice: 'Advice',
  living: 'Living',
  research: 'Research',
};

const FAA_SUBURB_SEARCH_RESULTS_PATH_REGEX =
  /^\/real-estate-(agents|agencies)\/[a-z0-9-]+-[a-z]{2,3}-\d{4}$/i;

const getSearchParamEntryPoint = (locationSearch: string): string | null => {
  const params = new URLSearchParams(locationSearch);
  const entryPoint = params.get('entry-point');

  if (!entryPoint) {
    return null;
  }

  try {
    return decodeURIComponent(entryPoint);
  } catch {
    return entryPoint;
  }
};

const getHomepageEntryPoint = (
  pathname: string,
  searchParams: URLSearchParams,
): string | null => {
  if (pathname === '/') {
    const mode = searchParams.get('mode');
    return HOMEPAGE_MODE_BY_PARAM[mode ?? ''] ?? 'Homepage - Buy';
  }

  const homepageView = HOMEPAGE_VIEW_BY_PATH[pathname];
  return homepageView ? `Homepage - ${homepageView}` : null;
};

const getSearchOrListingEntryPoint = (pathname: string): string | null => {
  const [modePathSegment, slug = ''] = pathname.split('/').filter(Boolean);
  const mode = MODE_BY_PATH_SEGMENT[modePathSegment];

  if (!mode) {
    return /-\d{6,}$/.test(modePathSegment) ? 'Listing' : null;
  }

  const isListing = /-\d{6,}$/.test(slug);
  const sourceType = isListing ? 'Listing' : 'Search Results';

  return `${sourceType} - ${mode}`;
};

const getEntryPointFromReferrer = (referrer: string): string => {
  if (!referrer) {
    return 'Direct';
  }

  let referrerUrl: URL;

  try {
    referrerUrl = new URL(referrer);
  } catch {
    return 'Direct';
  }

  const isDomainHost = /(^|\.)domain\.com\.au$/i.test(referrerUrl.hostname);

  if (!isDomainHost && referrerUrl.pathname === '/') {
    return referrerUrl.hostname;
  }

  const pathname = referrerUrl.pathname.replace(/\/$/, '') || '/';
  const pathSegments = pathname.split('/').filter(Boolean);
  const firstPathSegment = pathSegments[0];

  const homepageEntryPoint = getHomepageEntryPoint(
    pathname,
    referrerUrl.searchParams,
  );

  if (homepageEntryPoint) {
    return homepageEntryPoint;
  }

  const contentVertical = CONTENT_VERTICAL_BY_PATH_SEGMENT[firstPathSegment];
  if (contentVertical) {
    return `Content - ${contentVertical}`;
  }

  if (pathname === '/sell') {
    return 'Sell Landing Page';
  }

  if (pathname === '/property-profile') {
    return 'HPG Landing Page';
  }

  if (pathname.startsWith('/property-profile/')) {
    return 'Property Profile';
  }

  if (pathname.startsWith('/suburb-profile/')) {
    return 'Suburb Profile';
  }

  if (pathname === '/auction-results') {
    return 'Auction Results';
  }

  if (pathname.startsWith('/my-properties/')) {
    return 'DFO';
  }

  if (pathname === '/real-estate-agents') {
    return 'FAA Landing Page';
  }

  if (
    pathname.startsWith('/real-estate-agents/search-results') ||
    pathname.startsWith('/real-estate-agencies/search-results') ||
    FAA_SUBURB_SEARCH_RESULTS_PATH_REGEX.test(pathname)
  ) {
    return 'FAA Search Results';
  }

  if (!isDomainHost) {
    return referrerUrl.hostname;
  }

  const searchOrListingEntryPoint = getSearchOrListingEntryPoint(pathname);
  if (searchOrListingEntryPoint) {
    return searchOrListingEntryPoint;
  }

  return referrer;
};

const getEntryPoint = (options: EntryPointOptions = {}): string => {
  const isBrowser = typeof window !== 'undefined';
  const locationSearch =
    options.locationSearch ?? (isBrowser ? window.location.search : '');
  const queryEntryPoint = getSearchParamEntryPoint(locationSearch);

  if (queryEntryPoint) {
    return queryEntryPoint;
  }

  const referrer =
    options.serverReferer ??
    options.referrer ??
    (isBrowser ? document.referrer : '');
  return getEntryPointFromReferrer(referrer);
};

export default getEntryPoint;
